//@flow

import {
  getPopupBanners,
  createPopupBanner,
  deletePopupBanner,
  toggleEnabledPopupBannerService,
} from '../promises/popup-banner';

import {actions} from '../duck';

import {useEffect, useState} from 'react';
import {useActions} from '../../../utils/globalStore';
import {useSelector} from 'react-redux';
import {
  POPUP_LAYOUT_SIZE,
  type PopupBannerForm,
} from '../components/Sections/PopupBanner/PopupBannerForm/constants';
import type {RootState} from '../../';
import {compressImage} from '../../../utils/image';
import {uploadImageService} from '../../file-storage/promises';

const bannersSelector = (state: RootState) => state.homePage.popupBanners;

export function useInitializePopupBanner() {
  const setPopupBannerAction = useActions(actions.setSavedPopupBanners);
  const popupBanners = useSelector(bannersSelector);

  useEffect(() => {
    getPopupBanners()
      .then(banners => {
        setPopupBannerAction(banners || []);
      })
      .catch(error => {
        console.error(`Error getting banners: ${error}`);
        setPopupBannerAction([]);
      });
  }, [setPopupBannerAction]);

  return popupBanners;
}

export function usePopupBanner() {
  const popupBanners = useSelector(bannersSelector);

  return {
    popupBanners,
  };
}

const bannerActions = {
  deleteBannerAction: actions.deletePopupBanner,
  createBannerAction: actions.addPopupBanner,
  setEnabledBannerAction: actions.setEnabledPopupBanner,
};

type CreateBannerPayload = {
  bannerBlob?: Blob,
} & PopupBannerForm;

export function usePopupBannerActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<?{code: string, message: string}>(null);
  const {
    deleteBannerAction,
    createBannerAction,
    setEnabledBannerAction,
  } = useActions(bannerActions);

  const deleteBanner = (id: string) => {
    setLoading(true);
    return deletePopupBanner(id)
      .then(() => {
        deleteBannerAction(id);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createBanner = async (params: CreateBannerPayload) => {
    setLoading(true);

    try {
      const bannerBlob = params.bannerBlob;

      let imageId = undefined;

      if (bannerBlob) {
        const bannerImgFile = new File([bannerBlob], 'banner-popup.png');

        const [, bannerImgCompressed] = await compressImage(
          undefined,
          bannerImgFile,
          1,
          POPUP_LAYOUT_SIZE[params.contentPosition].width * 1.5,
          POPUP_LAYOUT_SIZE[params.contentPosition].height * 1.5
        );
        const [bannerImage] = await uploadImageService(
          // $FlowFixMe
          [bannerImgCompressed],
          'ui/popup-banner'
        );

        imageId = bannerImage.id;
      }

      const banner = await createPopupBanner({
        imageId,
        title: params.title,
        subtitle: params.subtitle,
        backgroundColor: params.backgroundColor,
        textColor: params.textColor,
        popUpPositionType: params.contentPosition,
        buttonText: params.buttonText,
        popUpButtonActionType: params.buttonAction,
        popUpButtonEntityType: params.buttonUrlType,
        buttonEntityIdentifier: params.buttonUrlIdentifier,
        buttonBackgroundColor: params.buttonBackgroundColor,
        buttonTextColor: params.buttonTextColor,
        popUpTriggerType: params.triggerType,
        popUpTriggerValue: Number(params.triggerValue),
        popUpTriggerValueCriteriaType: params.triggerValueCriteriaType,
        enabled: true,
      });

      createBannerAction(banner);
      return banner;
    } catch (exception) {
      console.error('Error creating banner:', exception);

      const errorCode = exception?.error?.error || 'UNKNOWN_ERROR';
      const errorMessage = exception?.error?.message || 'Error desconocido';

      setError({
        code: errorCode,
        message: errorMessage,
      });

      setTimeout(() => {
        setError(null);
      }, 5000);

      // Re-throw to allow form to handle it
      throw exception;
    } finally {
      setLoading(false);
    }
  };

  const setEnabledBanner = (id: string, enabled: boolean) => {
    setLoading(true);
    toggleEnabledPopupBannerService(id, enabled)
      .then(() => {
        setEnabledBannerAction(id, enabled);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    loading,
    error,
    deleteBanner,
    createBanner,
    setEnabledBanner,
  };
}
